
import jsPDF from "jspdf";
import { CalibriRegular } from "../fonts/calibri-normal";
import { CalibriBold } from "../fonts/calibri-bold";
import { CambriaRegular } from "../fonts/cambria-normal";
import { CambriaBold } from "../fonts/cambria-bold";

export const downloadInvoicePdf = (
  invoice: any, // Type applied here
  mode: "preview" | "download" = "download"
) => {
  const pdf = new jsPDF("p", "mm", "a4");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;

  // Numeric failsafe
  const safe = (v: any): number => {
    const num = Number(v);
    return isNaN(num) ? 0 : num;
  };

  // String failsafe
  const safeStr = (v: any): string => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "object") {
      return String(v.name || v.username || v.label || "[Object Error]");
    }
    return String(v);
  };

  const img = new Image();
  img.src = "/Logo_Encrypted.png";
  const isDraftInvoice =
    String(invoice?.invoice_status || "").toLowerCase() === "drafted";
  const invoiceNumberDisplay = isDraftInvoice
    ? "Drafted"
    : safeStr(invoice.invoice_number);

  /* ===== FONTS ===== */
  pdf.addFileToVFS("calibri.ttf", CalibriRegular);
  pdf.addFont("calibri.ttf", "Calibri", "normal");
  pdf.addFileToVFS("calibrib.ttf", CalibriBold);
  pdf.addFont("calibrib.ttf", "Calibri", "bold");
  pdf.addFileToVFS("cambria.ttf", CambriaRegular);
  pdf.addFont("cambria.ttf", "Cambria", "normal");
  pdf.addFileToVFS("cambriab.ttf", CambriaBold);
  pdf.addFont("cambriab.ttf", "Cambria", "bold");

  /* ================= HEADER ================= */
  pdf.addImage(img, "PNG", pageWidth - margin - 75, 12, 75, 18);

  pdf.setFont("Cambria", "normal");
  pdf.setFontSize(11);
  pdf.text("From", margin, 15);
  pdf.setFont("Cambria", "bold");
  pdf.text("Encrypted Infoweb", margin, 19);

  pdf.setFont("Cambria", "normal");
  pdf.setTextColor(120);
  pdf.text("729 - 742, R K World Tower,", margin, 23);
  pdf.text("Opp Shital Park BRTS, 150 Feet ,", margin, 27);
  pdf.text("Ring Road Rajkot - 360001", margin, 31);

  pdf.setTextColor(0, 0, 255);
  pdf.text("sales@encryptedinfoweb.com", margin, 35);
  pdf.setTextColor(0, 0, 0);
  pdf.text("+91 90238 49989", margin, 39);

  let y = 50;

  /* ================= TOP BOX ================= */
  /* ================= TOP BOX ================= */
  const leftY = y + 20;
  const customerName = safeStr(
    invoice.custom_customer_name || invoice.customer_name
  );
  const addressText = safeStr(invoice.address).replace(/\s+/g, " ").trim();
  const addressLines = pdf.splitTextToSize(addressText, pageWidth / 2 - 8);
  const addressStartY = leftY - 1;
  const addressLineHeight = 4.5;
  const addressBottomY =
    addressStartY + Math.max(addressLines.length - 1, 0) * addressLineHeight;
  const topContentBottomY = Math.max(leftY + 28, addressBottomY);
  const topBoxHeight = Math.max(50, topContentBottomY - y + 4);

  pdf.rect(margin, y, pageWidth - margin * 2, topBoxHeight);
  pdf.line(margin, y + 5.5, pageWidth - margin, y + 5.5);
  pdf.line(pageWidth / 2, y + 5.5, pageWidth / 2, y + topBoxHeight);
  pdf.setTextColor(30);
  pdf.setFont("Calibri", "bold");
  pdf.text("TAX INVOICE", pageWidth / 2, y + 4, { align: "center" });

  pdf.text("M/S.:", margin + 2, leftY - 10);
  pdf.text(customerName, margin + 2, leftY - 6);
  pdf.text(addressLines, margin + 2, addressStartY);

  pdf.text("Place Of Supply :", margin + 2, leftY + 24);
  pdf.text(safeStr(invoice.place_of_supply), margin + 32, leftY + 24);

  // Only show GSTIN No if valid GST data exists
  if (invoice.gstno && String(invoice.gstno).trim() !== "" && String(invoice.gstno) !== "-") {
    pdf.text("GSTIN No :", margin + 2, leftY + 28);
    pdf.text(safeStr(invoice.gstno), margin + 22, leftY + 28);
  }

  const rightX = pageWidth / 2 + 5;
  const rightY = y + 20;

  pdf.text("Invoice No:", rightX - 1, rightY - 10);
  pdf.text(invoiceNumberDisplay, rightX + 20, rightY - 10);

  pdf.text("Date:", rightX - 1, rightY - 6);
  pdf.text(safeStr(invoice.invoice_date), rightX + 10, rightY - 6);

  /* ================= TABLE ================= */
  y += topBoxHeight + 8;
  const colSr = 15;

  // Determine if any item has a GST value
  const hasGst = (Array.isArray(invoice.items) ? invoice.items : []).some(
    (item: any) => item.gst !== null && item.gst !== undefined && String(item.gst).trim() !== "" && String(item.gst).trim() !== "0"
  );

  const colGst    = hasGst ? 20 : 0;
  const colHsn    = 25;
  const colDesc   = hasGst ? 75 : 95; // give extra width to description when no GST
  const colAmount = pageWidth - margin * 2 - (colSr + colDesc + colHsn + colGst);

  const tableStartY = y;
  const headerHeight = 5.5;

  pdf.rect(margin, tableStartY, pageWidth - margin * 2, headerHeight);
  pdf.setTextColor(0);
  pdf.setFont("Calibri", "normal");
  pdf.text("Sr.No.", margin + 3, tableStartY + 4);
  pdf.text("Description", margin + colSr + 25, tableStartY + 4);
  pdf.text("HSN Code", margin + colSr + colDesc + 4, tableStartY + 4);
  if (hasGst) {
    pdf.text("GST %", margin + colSr + colDesc + colHsn + 4, tableStartY + 4);
  }
  pdf.text("Amount", pageWidth - margin - 30, tableStartY + 4);

  y += 8;
  const lineHeight = 5;

  const getCurrencySymbol = (currency: string) => {
    if (currency?.includes("INR") || currency === "₹ INR") return "₹";
    if (currency?.includes("USD") || currency === "$ USD") return "$";
    if (currency?.includes("EUR") || currency === "€ EUR") return "€";
    return currency || "$";
  };

  const symbol = getCurrencySymbol(invoice.currency);
  
  // Check if currency is INR
  const isINR = invoice.currency?.includes("INR") || invoice.currency === "â‚¹ INR";
  
  // Get exchange rate with safety checks
  let exchangeRate = 1;
  if (!isINR) {
    exchangeRate = safe(invoice?.currency_conversion?.exchange_rate || invoice.exchange_rate);
    // Prevent division by zero or invalid exchange rate
    if (exchangeRate === 0 || isNaN(exchangeRate) || !isFinite(exchangeRate)) {
      exchangeRate = 1; // Fallback to 1 if exchange rate is invalid
    }
  }  // Invoice values are already stored in the display currency.
  const displayAmount = (amount: number): number => {
    return isFinite(amount) ? amount : 0;
  };
  
  const items = Array.isArray(invoice.items) ? invoice.items : [];

  items.forEach((item: any, index: number) => {
    const descLines = pdf.splitTextToSize(safeStr(item.description), colDesc - 4);
    const hsnLines = pdf.splitTextToSize(safeStr(item.hsn), colHsn - 4);

    const rowHeight = Math.max(descLines.length, hsnLines.length) * lineHeight + 6;

    pdf.text(String(index + 1), margin + 5, y + 1);
    pdf.text(descLines, margin + colSr + 2, y + 1);
    pdf.text(hsnLines, margin + colSr + colDesc + 2, y + 1);
    if (hasGst) {
      pdf.text(safeStr(item.gst), margin + colSr + colDesc + colHsn + 4, y + 1);
    }

    const itemAmount = displayAmount(
      safe(!isINR ? item.originalAmount ?? item.amount : item.amount)
    );
    
    pdf.text(
      `${symbol} ${itemAmount.toFixed(2)}`,
      pageWidth - margin - 15,
      y + 1,
      { align: "right" }
    );
    y += rowHeight;
  });

  const minTableHeight = 50;
  const actualTableHeight = y - tableStartY;
  const finalTableHeight = Math.max(actualTableHeight, minTableHeight);
  const tableBottomY = tableStartY + finalTableHeight;

  let lineX = margin;
  pdf.line(lineX, tableStartY, lineX, tableBottomY);
  pdf.line((lineX += colSr), tableStartY, lineX, tableBottomY);
  pdf.line((lineX += colDesc), tableStartY, lineX, tableBottomY);
  pdf.line((lineX += colHsn), tableStartY, lineX, tableBottomY);
  if (hasGst) {
    pdf.line((lineX += colGst), tableStartY, lineX, tableBottomY + 10);
  }
  pdf.line(pageWidth - margin, tableStartY, pageWidth - margin, tableBottomY);

  /* ================= TOTAL ================= */
  y = tableBottomY;
  pdf.rect(margin, y, pageWidth - margin * 2, 10);
  pdf.text("TOTAL", pageWidth - margin - 70, y + 5);
  // Only show GSTIN if valid GST data exists in invoice
  if (invoice.gstno && String(invoice.gstno).trim() !== "" && String(invoice.gstno) !== "-") {
    pdf.text(`GSTIN NO. :${safeStr(invoice.gstno)}`, margin + 3, y + 5);
  }

  // Show taxable amount in the TOTAL row
  const totalAmount = displayAmount(safe(invoice.taxable_amount));
  
  pdf.text(
    `${symbol} ${totalAmount.toFixed(2)}`,
    pageWidth - margin - 15,
    y + 5,
    { align: "right" }
  );

  y += 18;

  /* ================= BANK BOX ================= */
  const footerReserved = 50;
  const availableHeight = pageHeight - y - footerReserved;
  const bankBoxHeight = Math.min(50, availableHeight);

  pdf.rect(margin, y, pageWidth - margin * 2, bankBoxHeight);
  const dividerX = pageWidth / 2 + 18 * 2 - margin;
  pdf.line(dividerX, y, dividerX, y + bankBoxHeight);

  pdf.text(`Bank Name      : ${safeStr(invoice.bank_name)}`, margin + 2.5, y + 4);
  pdf.text(`Bank A/C No    : ${safeStr(invoice.bank_account_number)}`, margin + 2.5, y + 9);
  pdf.text(`IFSC Code         : ${safeStr(invoice.ifsc_code)}`, margin + 2.5, y + 14);

  pdf.line(margin, y + 26, dividerX, y + 26);
  pdf.line(margin, y + 44, pageWidth - margin, y + 44);

  // Convert taxable amount if needed
  const taxableAmount = displayAmount(safe(invoice.taxable_amount));
  
  pdf.text("Taxable Amount :", dividerX + 2, y + 8);
  pdf.text(`${symbol}${taxableAmount.toFixed(2)}`, pageWidth - margin - 15, y + 8, { align: "right" });

  // Track current Y position for conditional tax sections
  let currentTaxY = y + 16;
  
  // Conditionally show CGST
  const cgstPercent = safe(invoice.cgst_percent);
  const cgstAmount = safe(invoice.cgst_amount);
  if (cgstAmount !== 0) {
    pdf.text(`CGST : ${cgstPercent}%`, dividerX + 2, currentTaxY);
    // Convert CGST amount if needed
    const convertedCgstAmount = displayAmount(cgstAmount);
    pdf.text(`${symbol}${convertedCgstAmount.toFixed(2)}`, pageWidth - margin - 15, currentTaxY, { align: "right" });
    currentTaxY += 6;
  }

  // Conditionally show SGST
  const sgstPercent = safe(invoice.sgst_percent);
  const sgstAmount = safe(invoice.sgst_amount);
  if (sgstAmount !== 0) {
    pdf.text(`SGST : ${sgstPercent}%`, dividerX + 2, currentTaxY);
    // Convert SGST amount if needed
    const convertedSgstAmount = displayAmount(sgstAmount);
    pdf.text(`${symbol}${convertedSgstAmount.toFixed(2)}`, pageWidth - margin - 15, currentTaxY, { align: "right" });
    currentTaxY += 6;
  }

  // Conditionally show IGST
  const igstPercent = safe(invoice.igst_percent);
  const igstAmount = safe(invoice.igst_amount);
  if (igstAmount !== 0) {
    pdf.text(`IGST : ${igstPercent}%`, dividerX + 2, currentTaxY);
    // Convert IGST amount if needed
    const convertedIgstAmount = displayAmount(igstAmount);
    pdf.text(`${symbol}${convertedIgstAmount.toFixed(2)}`, pageWidth - margin - 15, currentTaxY, { align: "right" });
    currentTaxY += 6;
  }

  // Add some spacing after tax sections
  currentTaxY += 2;

  // Convert round off amount if needed
  const roundOff = displayAmount(safe(invoice.round_off));
  
  pdf.text("Round Off :", dividerX + 2, currentTaxY);
  pdf.text(`${symbol}${roundOff.toFixed(2)}`, pageWidth - margin - 15, currentTaxY, { align: "right" });

  pdf.setFont("Calibri", "bold");
  pdf.text("Grand Total :", dividerX + 2, y + bankBoxHeight - 2);
  
  // Convert grand total if needed
  const grandTotal = displayAmount(safe(invoice.grand_total));
  
  pdf.text(`${symbol}${grandTotal.toFixed(2)}`, pageWidth - margin - 15, y + bankBoxHeight - 2, { align: "right" });

  y += bankBoxHeight;

  /* ================= TERMS ================= */
  pdf.rect(margin, y, pageWidth - margin * 2, 30);
  pdf.setFont("Calibri", "bold");
  pdf.text("Terms & Condition", margin + 5, y + 4);
  pdf.text(`1 "Subject to 'Rajkot' jurisdiction only.E.&.O.E"`, margin + 5, y + 9);
  pdf.text("For, Encrypted Infoweb", pageWidth / 2 + 20, y + 4);
  pdf.text("(Authorized Signatory)", pageWidth / 2 + 20, y + 27);
  
  const stampImg = new Image();
  stampImg.src = "/logo.jpeg";
  pdf.addImage(stampImg, "JPEG", pageWidth - margin - 65, y + 5, 20, 17);

  /* ================= FOOTER ================= */
  const footerY = pageHeight - 20;
  pdf.setFontSize(10);
  pdf.setTextColor(110);
  pdf.setFont("Cambria", "bold");
  pdf.text("Encrypted Infoweb", pageWidth / 2, footerY + 2, { align: "center" });
  
  pdf.setFontSize(9);
  pdf.setFont("Cambria", "normal");
  pdf.text("Ph. +91 90238 49989 | E. sales@encryptedinfoweb.com | W. www.encryptedinfoweb.com", pageWidth / 2, footerY + 6, { align: "center" });
  pdf.text("729 - 742, R K World Tower,Opp Shital Park BRTS, 150 Feet Ring Road Rajkot - 360001, Gujarat, India", pageWidth / 2, footerY + 10, { align: "center" });

  if (mode === "preview") {
    window.open(pdf.output("bloburl"), "_blank");
  } else {
    pdf.save(`${invoiceNumberDisplay}.pdf`);
  }
};









